import { useApi, useMessage } from 'app/context';
import { IDoctorAttachmentType, IDoctorSchema, IDoctorUpdate } from 'generated/axios';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from './useForm';
import { buildErrorDetails, buildErrorMessage, trimFileName } from 'app/utils';

export const useDoctorDetail = (id: string) => {
  const { doctorsApi } = useApi();

  const { error, success } = useMessage();

  const [loading, setLoading] = useState<boolean>(false);

  const [detail, setDetail] = useState<IDoctorSchema>();

  const [request, setRequest] = useState<IDoctorUpdate>();

  const buildRequestFromDetail = (data: IDoctorSchema): IDoctorUpdate => ({
    first_name: data.first_name,
    last_name: data.last_name,
  });

  const get = useCallback(() => {
    setLoading(true);
    doctorsApi
      .webGetDoctorByID(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorsApi, id]);

  useEffect(() => {
    get();
  }, [get]);

  const update = useCallback(
    (form: IDoctorUpdate) => {
      setLoading(true);
      doctorsApi
        .webUpdateDoctor(id, form)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('doctor.updateSuccess') as string });
          setDetail(resp.data);
          setRequest(buildRequestFromDetail(resp.data));
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
          updateForm.setErrors(buildErrorDetails(err));
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doctorsApi, id],
  );

  //FORM
  const updateForm = useForm<IDoctorUpdate>({
    defaultValues: request,
    onSubmit: update,
  });

  //UPLOAD SIGNATURE
  const uploadSignature = useCallback(
    (file: File) => {
      setLoading(true);
      doctorsApi
        .webUploadDoctorAttachment(IDoctorAttachmentType.Signature, id, file)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('doctor.uploadSignatureSuccess') as string });
          setDetail(resp.data);
          setRequest(buildRequestFromDetail(resp.data));
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doctorsApi, id],
  );

  //GET SIGNATURE
  const getSignature = () => {
    setLoading(true);
    doctorsApi
      .webDownloadDoctorAttachment(IDoctorAttachmentType.Signature, id, { responseType: 'blob' })
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        const blob = new Blob([resp.data as unknown as BlobPart]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = trimFileName(resp.headers?.['content-disposition']) ?? 'download';
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
  };

  //DELETE
  /* const deleteDoctor = useCallback(() => {
    setLoading(true);
    doctorsApi
      .webDeleteDoctor(id)
      .then((resp) => {
        if (resp.status === 204) {
          success({ message: t('doctor.deleteSuccess') as string });
          navigate(-1);
        } else {
          error({ message: buildErrorMessage(resp) });
        }
      })
      .finally(() => setLoading(false));
  }, [doctorsApi, id]); */

  //ACTIVATE
  const activate = useCallback(() => {
    setLoading(true);
    doctorsApi
      .webActivateDoctor(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('doctor.activateSuccess') as string });
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorsApi, id]);

  //DEACTIVATE
  const deactivate = useCallback(() => {
    setLoading(true);
    doctorsApi
      .webDectivateDoctor(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('doctor.deactivateSuccess') as string });
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorsApi, id]);

  return { detail, updateForm, loading, uploadSignature, getSignature, activate, deactivate };
};
